import axios, { type AxiosInstance } from 'axios';
import { onlineManager } from '@tanstack/react-query';
import qs from 'qs';
import { store } from '@/redux';
import { clearCredentials, syncRefreshedCredentials } from '@/redux/slices/authSlice';

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3431/v1';
const ACCESS_TOKEN_KEY = 'erp_access_token';
const REFRESH_TOKEN_KEY = 'erp_refresh_token';
const ACCESS_TOKEN_EXPIRES_AT_KEY = 'erp_access_token_expires_at';
const TERMINAL_REFRESH_ERROR_CODES = new Set([
  'INVALID_TOKEN',
  'REFRESH_REUSE_DETECTED',
  'SESSION_LIFETIME_EXCEEDED',
  'USER_TOKENS_REVOKED',
  'TENANT_TOKENS_REVOKED',
  'USER_NOT_ACTIVE',
]);
const TERMINAL_REFRESH_ERROR_MESSAGES = new Set([
  'NO_REFRESH_TOKEN',
  'REFRESH_MISSING_TOKEN',
]);

const PROACTIVE_REFRESH_MARGIN_SEC = 60;
const PROACTIVE_REFRESH_RETRY_DELAYS_MS = [5_000, 30_000, 120_000] as const;
const REFRESH_SYNC_CHANNEL = 'erp-auth-refresh-sync';
const REFRESH_SYNC_KEY = 'erp_auth_refresh_sync';
const REFRESH_OWNER_KEY = 'erp_auth_refresh_owner';
const REFRESH_FALLBACK_LOCK_TTL_MS = 15_000;
const REFRESH_FALLBACK_WAIT_MS = 20_000;
const TAB_INSTANCE_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

type RefreshSyncEvent =
  | { type: 'refresh_started'; correlation_id: string; started_at: number; owner_id: string }
  | { type: 'refresh_succeeded'; correlation_id: string; finished_at: number; owner_id: string; access_token: string }
  | { type: 'refresh_failed'; correlation_id: string; finished_at: number; owner_id: string; terminal: boolean };

type RefreshOwnerRecord = {
  owner_id: string;
  correlation_id: string;
  expires_at: number;
};

function getAccessTokenExpiresAt(): number | null {
  try {
    const raw = localStorage.getItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function setAccessTokenExpiresAt(expiresInSec: number): void {
  try {
    const expiresAt = Date.now() + expiresInSec * 1000;
    localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, String(expiresAt));
  } catch {
    /* localStorage unavailable */
  }
}

function clearAccessTokenExpiresAt(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
  } catch {
    /* localStorage unavailable */
  }
}

function persistAccessTokenExpiry(expiresInSec: unknown): void {
  if (typeof expiresInSec === 'number' && Number.isFinite(expiresInSec) && expiresInSec > 0) {
    setAccessTokenExpiresAt(expiresInSec);
    return;
  }

  clearAccessTokenExpiresAt();
}

function syncRefreshedCredentialsFromStorage(): void {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) {
    store.dispatch(clearCredentials());
    return;
  }
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  store.dispatch(
    syncRefreshedCredentials({
      accessToken,
      ...(refreshToken ? { refreshToken } : {}),
    }),
  );
}

export function activateProactiveAccessTokenRefresh(expiresInSec: unknown): void {
  resetProactiveRefreshFailures();
  persistAccessTokenExpiry(expiresInSec);
  scheduleAccessTokenRefresh();
}

export function getApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const code = error.response?.data?.error?.code;
  return typeof code === 'string' ? code : undefined;
}

export function isTerminalRefreshFailure(error: unknown): boolean {
  const code = getApiErrorCode(error);
  if (code && TERMINAL_REFRESH_ERROR_CODES.has(code)) return true;
  if (error instanceof Error && TERMINAL_REFRESH_ERROR_MESSAGES.has(error.message)) {
    return true;
  }
  return false;
}

export function shouldRedirectToLoginAfterRefreshFailure(error: unknown): boolean {
  return isTerminalRefreshFailure(error);
}

function clearAuthTokensAndRedirect(): void {
  resetProactiveRefreshFailures();
  if (_accessTokenRefreshTimer) {
    clearTimeout(_accessTokenRefreshTimer);
    _accessTokenRefreshTimer = null;
  }
  store.dispatch(clearCredentials());
  try {
    const raw = localStorage.getItem('persist:erp-tenant');
    if (raw) {
      const parsed = JSON.parse(raw);
      parsed.auth = JSON.stringify({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
      });
      localStorage.setItem('persist:erp-tenant', JSON.stringify(parsed));
    }
  } catch {
    /* ignore */
  }
  window.location.href = '/auth/login';
}

function handleRefreshFailure(error: unknown): void {
  if (shouldRedirectToLoginAfterRefreshFailure(error)) {
    clearAuthTokensAndRedirect();
  }
}

function shouldRefreshProactively(): boolean {
  const expiresAt = getAccessTokenExpiresAt();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt - PROACTIVE_REFRESH_MARGIN_SEC * 1000;
}

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(REFRESH_SYNC_CHANNEL);
}

function readRefreshOwner(): RefreshOwnerRecord | null {
  try {
    const raw = localStorage.getItem(REFRESH_OWNER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RefreshOwnerRecord;
    if (!parsed?.owner_id || !parsed?.expires_at || parsed.expires_at <= Date.now()) {
      localStorage.removeItem(REFRESH_OWNER_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeRefreshOwner(owner: RefreshOwnerRecord): void {
  localStorage.setItem(REFRESH_OWNER_KEY, JSON.stringify(owner));
}

function clearRefreshOwner(ownerId: string): void {
  const current = readRefreshOwner();
  if (current?.owner_id === ownerId) {
    localStorage.removeItem(REFRESH_OWNER_KEY);
  }
}

function publishRefreshSync(event: RefreshSyncEvent): void {
  try {
    localStorage.setItem(REFRESH_SYNC_KEY, JSON.stringify(event));
  } catch {
    /* localStorage unavailable */
  }

  const channel = getBroadcastChannel();
  if (!channel) return;
  try {
    channel.postMessage(event);
  } finally {
    channel.close();
  }
}

function waitForRefreshResult(correlationId: string): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const channel = getBroadcastChannel();

    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (channel) channel.close();
      window.removeEventListener('storage', handleStorage);
      resolve(value);
    };

    const handleEvent = (event: RefreshSyncEvent) => {
      if (event.correlation_id !== correlationId) return;
      if (event.type === 'refresh_succeeded') {
        finish(event.access_token);
        return;
      }
      if (event.type === 'refresh_failed') {
        finish(null);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== REFRESH_SYNC_KEY || !event.newValue) return;
      try {
        handleEvent(JSON.parse(event.newValue) as RefreshSyncEvent);
      } catch {
        /* ignore malformed event */
      }
    };

    if (channel) {
      channel.onmessage = (message: MessageEvent<RefreshSyncEvent>) => handleEvent(message.data);
    }
    window.addEventListener('storage', handleStorage);
    timeoutId = setTimeout(() => finish(null), REFRESH_FALLBACK_WAIT_MS);
  });
}

async function refreshWithoutWebLocks(accessAtRequest: string | null): Promise<string> {
  const latestAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (latestAccess && latestAccess !== accessAtRequest) {
    return latestAccess;
  }

  const existingOwner = readRefreshOwner();
  if (existingOwner && existingOwner.owner_id !== TAB_INSTANCE_ID) {
    const waitedToken = await waitForRefreshResult(existingOwner.correlation_id);
    if (waitedToken) return waitedToken;
  }

  const correlationId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const ownerRecord: RefreshOwnerRecord = {
    owner_id: TAB_INSTANCE_ID,
    correlation_id: correlationId,
    expires_at: Date.now() + REFRESH_FALLBACK_LOCK_TTL_MS,
  };
  writeRefreshOwner(ownerRecord);

  const confirmedOwner = readRefreshOwner();
  if (confirmedOwner?.owner_id !== TAB_INSTANCE_ID || confirmedOwner.correlation_id !== correlationId) {
    const waitedToken = await waitForRefreshResult(confirmedOwner?.correlation_id ?? correlationId);
    if (waitedToken) return waitedToken;
  }

  publishRefreshSync({
    type: 'refresh_started',
    correlation_id: correlationId,
    started_at: Date.now(),
    owner_id: TAB_INSTANCE_ID,
  });

  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('NO_REFRESH_TOKEN');
    const accessToken = await postRefresh(refreshToken);
    publishRefreshSync({
      type: 'refresh_succeeded',
      correlation_id: correlationId,
      finished_at: Date.now(),
      owner_id: TAB_INSTANCE_ID,
      access_token: accessToken,
    });
    return accessToken;
  } catch (error) {
    publishRefreshSync({
      type: 'refresh_failed',
      correlation_id: correlationId,
      finished_at: Date.now(),
      owner_id: TAB_INSTANCE_ID,
      terminal: shouldRedirectToLoginAfterRefreshFailure(error),
    });
    throw error;
  } finally {
    clearRefreshOwner(TAB_INSTANCE_ID);
  }
}

let apiUnavailable = false;

export class ApiUnavailableError extends Error {
  code = 'API_UNAVAILABLE';
  isApiUnavailableError = true;
  /**
   * Request đã kịp rời trình duyệt trước khi hỏng hay chưa.
   *
   * `false` = chốt đang bật và request bị từ chối NGAY tại request interceptor;
   * BE chắc chắn chưa nhận gì. `true` = request đã gửi rồi mới đứt mạng, không
   * ai biết BE đã ghi hay chưa. Người gọi có thao tác không idempotent (POS ghi
   * hoá đơn) đọc cờ này để biết có được phép làm lại từ đầu hay không.
   */
  reachedServer: boolean;

  constructor(message = 'API server is unavailable', reachedServer = false) {
    super(message);
    this.name = 'ApiUnavailableError';
    this.reachedServer = reachedServer;
  }
}

export function isApiUnavailableError(error: unknown): error is ApiUnavailableError {
  return Boolean(
    error &&
      typeof error === 'object' &&
      (error as { isApiUnavailableError?: unknown }).isApiUnavailableError,
  );
}

/**
 * Sau bao lâu thì TỰ mở lại cổng để thử gọi API tiếp.
 *
 * Chốt chặn này BẮT BUỘC phải tự gỡ. Khi `apiUnavailable` bật, request
 * interceptor từ chối mọi lời gọi TRƯỚC khi ra mạng, nên không lời gọi nào có
 * thể thành công để `resumeApiAvailability` được chạy — cờ tự khoá chính nó.
 * Bản cũ chỉ mở lại nhờ sự kiện `focus`/`online`; người dùng không rời tab thì
 * cả ứng dụng đứng im tới khi tải lại trang.
 *
 * Nặng hơn nữa: `onlineManager.setOnline(false)` khiến TanStack Query TẠM DỪNG
 * mutation (networkMode mặc định là 'online'). Mutation bị dừng không gửi request
 * và cũng không reject — `mutateAsync` treo vĩnh viễn, nên màn hình không có
 * request, không có thông báo lỗi, không có gì cả. Đúng triệu chứng của lỗi tải
 * tài liệu nhân viên.
 *
 * API chết thật thì lần thử kế tiếp lại thất bại và cờ bật lại — không mất gì.
 */
const API_UNAVAILABLE_RETRY_MS = 15_000;
let apiUnavailableTimer: ReturnType<typeof setTimeout> | undefined;

function markApiUnavailable(): void {
  if (!apiUnavailable) {
    apiUnavailable = true;
    onlineManager.setOnline(false);
  }
  if (typeof window === 'undefined') return;
  clearTimeout(apiUnavailableTimer);
  apiUnavailableTimer = setTimeout(resumeApiAvailability, API_UNAVAILABLE_RETRY_MS);
}

function resumeApiAvailability(): void {
  clearTimeout(apiUnavailableTimer);
  if (apiUnavailable) {
    apiUnavailable = false;
    onlineManager.setOnline(true);
  }
}

function isNetworkUnavailableError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return false;
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.message === 'Network Error'
  );
}

/**
 * Cho phép một request TỰ LOẠI khỏi cơ chế phát hiện "mất API".
 *
 * Cần cho các lượt tải file nhị phân: tiện ích tải xuống (IDM, FDM…) chen vào
 * giữa, cướp luồng dữ liệu rồi huỷ request của trình duyệt. Với axios đó là lỗi
 * KHÔNG có `response` (`ERR_NETWORK`/`ECONNABORTED`) — trùng đúng dấu hiệu của
 * "server chết", nên `markApiUnavailable()` bắn nhầm và cả ứng dụng chuyển sang
 * chế độ offline dù backend vừa trả 200. Xem `utils/openBlob.ts`.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    skipApiAvailabilityTracking?: boolean;
  }
}

function attachAvailabilityInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    if (apiUnavailable) {
      return Promise.reject(new ApiUnavailableError());
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      resumeApiAvailability();
      return response;
    },
    (error) => {
      if (isNetworkUnavailableError(error) && !error.config?.skipApiAvailabilityTracking) {
        markApiUnavailable();
        return Promise.reject(new ApiUnavailableError(error.message, true));
      }
      return Promise.reject(error);
    },
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('focus', resumeApiAvailability);
  window.addEventListener('online', resumeApiAvailability);
}

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }),
});
attachAvailabilityInterceptors(api);

// Shared refresh promise (in-tab) — fallback cho browser không hỗ trợ Web Locks API.
// Web Locks API là cơ chế chính để sync giữa nhiều tab cùng origin (ngăn race condition
// đa tab triggers BE REFRESH_REUSE_DETECTED revoke chain — ADR-012 §D7).
let _refreshPromise: Promise<string> | null = null;

const REFRESH_LOCK_NAME = 'erp-auth-refresh';

async function postRefresh(refreshToken: string): Promise<string> {
  // Bare axios — tránh recursive interceptor nếu /auth/refresh tự trả 401.
  // BE response (ADR-013 D2): { data: { access_token, refresh_token, ... }, success: true }
  const res = await axios.post(
    `${API_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  const payload = res.data?.data ?? res.data ?? {};
  const accessToken: string | undefined = payload.access_token;
  const newRefreshToken: string | undefined = payload.refresh_token;

  if (!accessToken) throw new Error('REFRESH_MISSING_TOKEN');

  store.dispatch(
    syncRefreshedCredentials({
      accessToken,
      ...(newRefreshToken ? { refreshToken: newRefreshToken } : {}),
    }),
  );
  activateProactiveAccessTokenRefresh(payload.expires_in);

  return accessToken;
}

/**
 * Refresh access token, sync giữa các tab qua Web Locks API.
 *
 * `accessAtRequest` = access token gắn vào request 401 ban đầu. Khi vào lock, kiểm tra
 * localStorage: nếu access đã đổi (do tab khác refresh xong trước) → dùng luôn, không
 * gọi /auth/refresh thêm lần nữa (tránh thừa request và race với BE rotation).
 *
 * Fallback in-tab `_refreshPromise` cho browser không support Web Locks (Safari <15.4).
 */
async function doRefresh(accessAtRequest: string | null): Promise<string> {
  const refreshOnce = async (): Promise<string> => {
    const latestAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (latestAccess && latestAccess !== accessAtRequest) {
      // Tab khác đã refresh xong trong lúc tab này chờ lock — dùng access mới luôn.
      return latestAccess;
    }
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('NO_REFRESH_TOKEN');
    return postRefresh(refreshToken);
  };

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, refreshOnce);
  }

  // Fallback in-tab promise vẫn giữ để chống duplicate trong cùng execution context,
  // nhưng implementation bên trong đã có thêm cross-tab coordination.
  if (!_refreshPromise) {
    _refreshPromise = refreshWithoutWebLocks(accessAtRequest).finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}

/**
 * Khôi phục handshake realtime khi server chủ động ngắt socket vì access token cũ.
 * Luôn đồng bộ token trả về vào Redux, kể cả khi một tab khác đã refresh trước và
 * `doRefresh` chỉ lấy token mới từ localStorage mà không gọi lại endpoint refresh.
 */
export async function refreshAccessTokenForRealtime(
  accessAtRequest: string,
): Promise<string> {
  try {
    const accessToken = await doRefresh(accessAtRequest);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    store.dispatch(
      syncRefreshedCredentials({
        accessToken,
        ...(refreshToken ? { refreshToken } : {}),
      }),
    );
    return accessToken;
  } catch (error) {
    handleRefreshFailure(error);
    throw error;
  }
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // EG-369: owner/manager mở ca POS với store_id chọn → persist sessionStorage
    // → mọi request /pos/* tự inject `?store_id` để BE scope theo store. Cashier
    // có JWT.sid thì BE resolve từ token, FE không inject (tránh ghi đè).
    // Naming `store_id` (snake_case) theo ADR-013 — query string snake_case.
    const url = String(config.url ?? '');
    if (url.startsWith('/pos') || url.includes('/pos/')) {
      try {
        const shiftStoreId = sessionStorage.getItem('pos.shiftStoreId');
        if (shiftStoreId) {
          // Caller truyền `store_id` tường minh thì tôn trọng (vd màn sửa đơn cần
          // scope theo store của ĐƠN, khác store của ca) — chỉ inject khi thiếu.
          config.params = { store_id: shiftStoreId, ...(config.params ?? {}) };
        }
      } catch {
        /* sessionStorage unavailable (SSR / private mode) */
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const SECRET_LOG_FIELD_KEYS = new Set(['claim', 'connect_claim']);

/**
 * Che field bí mật (vd `claim`/`connect_claim` của Omnichat connect flow — hợp đồng
 * §3 bắt buộc gửi qua cả query lẫn body) trước khi `logApiError` in `params`/`data`
 * ra console ở bản dev.
 */
export function maskSecretLogFields<T>(input: T): T {
  if (!input || typeof input !== 'object') return input;
  const out = { ...(input as Record<string, unknown>) };
  for (const key of Object.keys(out)) {
    if (SECRET_LOG_FIELD_KEYS.has(key)) out[key] = '***';
  }
  return out as T;
}

function logApiError(error: unknown): void {
  if (!import.meta.env.DEV || !axios.isAxiosError(error)) return;
  const cfg = error.config;
  if (!cfg) return;
  const req = {
    method: cfg.method?.toUpperCase(),
    // Query có thể chứa connect_claim/token; chỉ log path để chẩn đoán route.
    url: cfg.url?.split(/[?#]/, 1)[0],
  };
  const res = error.response
    ? {
        status: error.response.status,
        code:
          typeof error.response.data?.error?.code === 'string'
            ? error.response.data.error.code
            : undefined,
      }
    : null;
  console.groupCollapsed(`[API] ${req.method} ${req.url} — ${res?.status ?? 'NETWORK_ERROR'}`);
  console.error('Request:', req);
  if (res) console.error('Response:', res);
  console.groupEnd();
}

/**
 * Request tải file dùng `responseType: 'blob'`, nên KHI LỖI body lỗi cũng về
 * dạng Blob. Mọi chỗ đọc `response.data.error.message` vì thế đều trượt và người
 * dùng chỉ còn thấy thông báo mặc định chung chung — lỗi thật ("Đơn hàng chưa có
 * mã vận đơn", "Best từ chối in waybill"…) bị nuốt mất.
 *
 * Đọc blob ra JSON ngay tại đây để phần còn lại của app xử lý như response lỗi
 * bình thường, không phải sửa từng service tải file.
 */
async function unwrapBlobErrorBody(error: unknown): Promise<void> {
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return;
  // Blob nhị phân thật (pdf/xlsx) thì để nguyên — chỉ JSON/text mới có message.
  if (data.type && !/json|text/i.test(data.type)) return;
  try {
    response!.data = JSON.parse(await data.text());
  } catch {
    /* không phải JSON — giữ nguyên blob */
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    await unwrapBlobErrorBody(error);
    logApiError(error);
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authHeader: string | undefined =
          originalRequest.headers?.Authorization;
        const accessAtRequest = authHeader?.startsWith('Bearer ')
          ? authHeader.slice('Bearer '.length)
          : null;
        const accessToken = await doRefresh(accessAtRequest);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        handleRefreshFailure(refreshError);
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.data?.error?.message) {
      error.message = error.response.data.error.message;
    }

    return Promise.reject(error);
  }
);

const noAuthApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});
attachAvailabilityInterceptors(noAuthApi);

noAuthApi.interceptors.response.use(
  (response) => response,
  (error) => {
    logApiError(error);
    return Promise.reject(error);
  },
);

let _accessTokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let _proactiveRefreshFailureCount = 0;

function resetProactiveRefreshFailures(): void {
  _proactiveRefreshFailureCount = 0;
}

function scheduleProactiveRefreshRetry(): void {
  const delay = PROACTIVE_REFRESH_RETRY_DELAYS_MS[_proactiveRefreshFailureCount];
  if (delay === undefined) return;
  _proactiveRefreshFailureCount += 1;
  scheduleAccessTokenRefresh(delay);
}

async function refreshAccessTokenProactively(): Promise<void> {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) {
    resetProactiveRefreshFailures();
    return;
  }
  if (!shouldRefreshProactively()) {
    // Token/expiry có thể vừa được tab khác xoay trong lúc timer cũ đang chạy.
    // Luôn nhường sang mốc mới thay vì để tab này mất proactive timer vĩnh viễn.
    resetProactiveRefreshFailures();
    scheduleAccessTokenRefresh();
    return;
  }

  try {
    await doRefresh(accessToken);
    // `doRefresh` có thể chỉ nhận token tab khác vừa ghi, không đi qua `postRefresh`
    // của tab này; vì vậy caller luôn tự bảo đảm có timer kế tiếp.
    resetProactiveRefreshFailures();
    scheduleAccessTokenRefresh();
  } catch (refreshError) {
    if (!shouldRedirectToLoginAfterRefreshFailure(refreshError)) {
      scheduleProactiveRefreshRetry();
    }
    handleRefreshFailure(refreshError);
  }
}

export async function runBootstrapRefreshIfNeeded(): Promise<void> {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const expiresAt = getAccessTokenExpiresAt();

  if (!accessToken || !refreshToken || !expiresAt) {
    scheduleAccessTokenRefresh();
    return;
  }

  if (!shouldRefreshProactively()) {
    scheduleAccessTokenRefresh();
    return;
  }

  try {
    await doRefresh(accessToken);
    resetProactiveRefreshFailures();
    scheduleAccessTokenRefresh();
  } catch (refreshError) {
    if (!shouldRedirectToLoginAfterRefreshFailure(refreshError)) {
      scheduleProactiveRefreshRetry();
      return;
    }

    if (shouldRedirectToLoginAfterRefreshFailure(refreshError)) {
      clearAuthTokensAndRedirect();
      throw refreshError;
    }
  }
}

export function scheduleAccessTokenRefresh(delayOverrideMs?: number): void {
  if (_accessTokenRefreshTimer) {
    clearTimeout(_accessTokenRefreshTimer);
    _accessTokenRefreshTimer = null;
  }

  const expiresAt = getAccessTokenExpiresAt();
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!expiresAt || !refreshToken) return;

  const now = Date.now();
  const refreshAt = expiresAt - PROACTIVE_REFRESH_MARGIN_SEC * 1000;
  const delay = delayOverrideMs ?? Math.max(refreshAt - now, 1000);

  _accessTokenRefreshTimer = setTimeout(async () => {
    _accessTokenRefreshTimer = null;

    await refreshAccessTokenProactively();
  }, delay);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!accessToken) return;

    if (shouldRefreshProactively()) {
      refreshAccessTokenProactively()
        .then(() => scheduleAccessTokenRefresh())
        .catch(() => {});
    } else {
      scheduleAccessTokenRefresh();
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (
      event.key !== ACCESS_TOKEN_KEY &&
      event.key !== REFRESH_TOKEN_KEY &&
      event.key !== ACCESS_TOKEN_EXPIRES_AT_KEY
    ) return;
    if (event.key === ACCESS_TOKEN_KEY || event.key === REFRESH_TOKEN_KEY) {
      syncRefreshedCredentialsFromStorage();
    }
    resetProactiveRefreshFailures();
    scheduleAccessTokenRefresh();
  });
  scheduleAccessTokenRefresh();
}

export default api;
export { noAuthApi };
